import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Bell,
  Search,
  Menu,
  LogOut,
  MessageSquare,
  CreditCard
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { TelegramConnectDialog } from '@/components/users/TelegramConnectDialog';
import { PaymentBotConnectDialog } from '@/components/users/PaymentBotConnectDialog';
import { checkTelegramConnection } from '@/services/telegram/connectionUtils';
import { getUserPaymentBotConnection } from '@/services/telegram/paymentNotificationService';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";

interface HeaderProps {
  toggleSidebar: () => void;
  sidebarOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({ toggleSidebar, sidebarOpen }) => {
  const { user, session, clearAuthState, userRole, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false);
  const [paymentBotDialogOpen, setPaymentBotDialogOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isPaymentBotConnected, setIsPaymentBotConnected] = useState(false);

  // Generate breadcrumbs from path
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbs = pathSegments.map((segment, index) => {
    const path = `/${pathSegments.slice(0, index + 1).join('/')}`;
    const name = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
    return { name, path, isLast: index === pathSegments.length - 1 };
  });

  // Hide Telegram features for benchmark users
  const showTelegramFeatures = userRole !== 'benchmark';
  // Show Payment Bot features for finance and admin users
  const showPaymentBotFeatures = ['admin', 'finance'].includes(userRole || '');
  // Hide settings for benchmark users
  const showSettings = userRole !== 'benchmark';

  useEffect(() => {
    const checkTelegramStatus = async () => {
      if (user?.id && showTelegramFeatures) {
        const connected = await checkTelegramConnection(user.id);
        setIsConnected(connected);
      }
    };

    const checkPaymentBotStatus = async () => {
      if (user?.id && showPaymentBotFeatures) {
        const conn = await getUserPaymentBotConnection(user.id);
        setIsPaymentBotConnected(!!conn);
      }
    };

    if (user && showTelegramFeatures) {
      checkTelegramStatus();
    }
    if (user && showPaymentBotFeatures) {
      checkPaymentBotStatus();
    }
  }, [user, telegramDialogOpen, paymentBotDialogOpen, showTelegramFeatures, showPaymentBotFeatures]);

  const handleLogout = async () => {
    try {
      clearAuthState();
      try {
        await signOut();
      } catch (err: any) {
        if (!err?.message?.includes('Auth session missing')) throw err;
      }
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error: any) {
      toast.error('Error logging out: ' + error.message);
    }
  };

  return (
    <header className="sticky top-0 z-30 bg-background/60 backdrop-blur-xl border-b border-sidebar-border/30 h-16 transition-all duration-300">
      <div className="h-full px-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="text-muted-foreground hover:text-harvest-primary transition-colors h-9 w-9 rounded-xl hover:bg-harvest-primary/10"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="hidden lg:block h-6 w-[1px] bg-border/50" />

          <Breadcrumb className="hidden md:block">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => navigate('/dashboard')}
                  className="cursor-pointer hover:text-harvest-primary transition-colors flex items-center gap-2"
                >
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbs.map((crumb) => (
                <React.Fragment key={crumb.path}>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {crumb.isLast ? (
                      <BreadcrumbPage className="font-semibold text-harvest-primary">{crumb.name}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink
                        onClick={() => navigate(crumb.path)}
                        className="cursor-pointer hover:text-harvest-primary transition-colors"
                      >
                        {crumb.name}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex relative max-w-[200px] xl:max-w-md group">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground group-focus-within:text-harvest-primary transition-colors">
              <Search className="h-4 w-4" />
            </div>
            <Input
              type="search"
              placeholder="Instant search..."
              className="pl-9 h-9 rounded-xl border-border/50 bg-background/50 focus-visible:ring-harvest-primary/30 transition-all group-hover:border-harvest-primary/50"
            />
          </div>

          <div className="h-8 w-[1px] bg-border/50 mx-2 hidden sm:block" />

          <Button
            variant="ghost"
            size="icon"
            className="relative text-muted-foreground hover:text-harvest-primary h-9 w-9 rounded-xl hover:bg-harvest-primary/10"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5" />
            <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-harvest-primary border-2 border-background shadow-sm animate-pulse"></span>
          </Button>

          {user ? (
            <div className="flex items-center gap-2">
              <div className="hidden xl:flex flex-col items-end mr-1">
                <span className="text-xs font-semibold leading-none">{`${user.first_name || ''} ${user.last_name || 'User'}`}</span>
                <span className="text-[10px] text-muted-foreground capitalize font-medium">{userRole}</span>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-xl h-9 w-9 p-0 overflow-hidden border-border/50 hover:border-harvest-primary/30 hover:ring-2 hover:ring-harvest-primary/10 transition-all"
                  >
                    <div className="h-full w-full bg-harvest-primary/10 flex items-center justify-center text-harvest-primary font-bold text-xs uppercase">
                      {user.first_name ? user.first_name[0] : 'U'}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 p-2 rounded-xl border-border/50 shadow-2xl backdrop-blur-xl bg-background/80">
                  <DropdownMenuLabel className="font-normal px-4 py-3">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-bold leading-none">
                        {`${user.first_name || ''} ${user.last_name || 'User'}`}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground italic">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="opacity-50" />
                  {showSettings && (
                    <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer rounded-lg focus:bg-harvest-primary/10 focus:text-harvest-primary py-2 px-3">
                      Settings
                    </DropdownMenuItem>
                  )}
                  {showTelegramFeatures && (
                    <DropdownMenuItem onClick={() => setTelegramDialogOpen(true)} className="cursor-pointer rounded-lg focus:bg-harvest-primary/10 focus:text-harvest-primary py-2 px-3">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      {isConnected ? 'Update Telegram' : 'Connect Telegram'}
                    </DropdownMenuItem>
                  )}
                  {showPaymentBotFeatures && (
                    <DropdownMenuItem onClick={() => setPaymentBotDialogOpen(true)} className="cursor-pointer rounded-lg focus:bg-harvest-primary/10 focus:text-harvest-primary py-2 px-3">
                      <CreditCard className="mr-2 h-4 w-4" />
                      {isPaymentBotConnected ? 'Payment Bot OK' : 'Connect Payment Bot'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="opacity-50" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer rounded-lg focus:bg-harvest-error/10 focus:text-harvest-error text-harvest-error py-2 px-3 text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {showTelegramFeatures && (
                <TelegramConnectDialog
                  open={telegramDialogOpen}
                  onOpenChange={setTelegramDialogOpen}
                />
              )}
              {showPaymentBotFeatures && (
                <PaymentBotConnectDialog
                  open={paymentBotDialogOpen}
                  onOpenChange={setPaymentBotDialogOpen}
                />
              )}
            </div>
          ) : (
            <Button
              variant="default"
              onClick={() => navigate('/login')}
              className="rounded-xl bg-harvest-primary hover:bg-harvest-primary/90 text-white shadow-lg shadow-harvest-primary/20"
            >
              Sign In
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
